// The script injected right after the CSP meta in every preview (PRD FR-14, FR-16, FR-17).
// It runs inside the sandboxed frame and talks to the app only through postMessage.
// The app must check `event.source === iframe.contentWindow` and `data.__buildlab === 1`.

export type BridgeMessage =
  | { __buildlab: 1; kind: "ready" }
  | { __buildlab: 1; kind: "console"; level: "log" | "warn" | "error"; text: string }
  | { __buildlab: 1; kind: "error"; level: "error"; text: string; file?: string; line?: number }
  | { __buildlab: 1; kind: "pong"; id: number }
  | { __buildlab: 1; kind: "saveData"; key: string; value: unknown }
  | { __buildlab: 1; kind: "blocked"; what: string; detail: string }
  | { __buildlab: 1; kind: "navigate"; page: string; hash: string }
  | { __buildlab: 1; kind: "watch"; id: number; values: Record<string, WatchedValue>; runs: Record<string, number> }
  | { __buildlab: 1; kind: "test"; id: number; pass: boolean; detail: string }
  | { __buildlab: 1; kind: "getData"; id: number; source: string; params: unknown }
  | { __buildlab: 1; kind: "classData"; id: number; op: "get" | "set"; key: string; value?: unknown }
  /** The page's content size and the frame's viewport, for fitting the game to its area (fit.ts). */
  | { __buildlab: 1; kind: "size"; w: number; h: number; vw: number; vh: number }
  /** Esc was pressed inside the frame (it leaves Play big; the app can't hear keys while the game has focus). */
  | { __buildlab: 1; kind: "escape" };

/** The app's answer to a shareData / getShared call, posted back into the frame. */
export interface ClassDataReply {
  __buildlabClassData: 1;
  id: number;
  ok: boolean;
  value?: unknown;
  error?: string;
}

/** A program value as the code view shows it ("Things in your game"). */
export type WatchedValue =
  | { t: "unset" }
  | { t: "none" }
  | { t: "number" | "string" | "boolean"; text: string }
  | { t: "element"; tag: string; text: string; in?: { list: string; index: number } }
  | { t: "list"; count: number; tag?: string; text?: string }
  | { t: "drawing" }
  | { t: "function" }
  | { t: "object"; text: string };

/** JSON safe to embed inside an inline <script>. */
export function scriptJson(value: unknown): string {
  return JSON.stringify(value).replace(/</g, "\\u003c").replace(/\u2028/g, "\\u2028").replace(/\u2029/g, "\\u2029");
}

/** `pages`: the HTML of every page in a multi-page site, so a play-test can look at the other pages. */
export function bridgeScript(savedData: Record<string, unknown> = {}, pages: Record<string, string> = {}): string {
  return `(function () {
  "use strict";
  var post = function (msg) { msg.__buildlab = 1; try { parent.postMessage(msg, "*"); } catch (e) {} };
  var fmt = function (v) {
    try {
      if (typeof v === "string") return v;
      if (v instanceof Error) return v.name + ": " + v.message;
      var s = JSON.stringify(v);
      return s === undefined ? String(v) : s;
    } catch (e) { return String(v); }
  };
  ["log", "info", "warn", "error"].forEach(function (level) {
    var original = console[level];
    console[level] = function () {
      var text = Array.prototype.map.call(arguments, fmt).join(" ").slice(0, 2000);
      post({ kind: "console", level: level === "info" ? "log" : level, text: text });
      return original.apply(console, arguments);
    };
  });
  var where = function (stack) {
    var m = /([A-Za-z0-9_.-]+\\.js):(\\d+):(\\d+)/.exec(stack || "");
    return m ? { file: m[1], line: Number(m[2]) } : null;
  };
  window.addEventListener("error", function (e) {
    var err = e.error;
    var loc = (err && where(err.stack)) || null;
    post({
      kind: "error", level: "error",
      text: (err && err.name ? err.name + ": " + err.message : String(e.message)).slice(0, 2000),
      file: loc ? loc.file : undefined, line: loc ? loc.line : undefined
    });
  });
  window.addEventListener("unhandledrejection", function (e) {
    var loc = e.reason && where(e.reason.stack);
    post({ kind: "error", level: "error", text: ("Unhandled promise rejection: " + fmt(e.reason)).slice(0, 2000),
      file: loc ? loc.file : undefined, line: loc ? loc.line : undefined });
  });
  // Code view: how often each function ran (calls are added by the preview build) and the
  // current values of the program's top-level variables, read by name on request.
  var runs = {};
  window.__blRan = function (key) { runs[key] = (runs[key] || 0) + 1; };
  var describe = function (v) {
    if (v === undefined || v === null) return { t: "none" };
    var type = typeof v;
    if (type === "number" || type === "boolean") return { t: type, text: String(v) };
    if (type === "string") return { t: "string", text: v.slice(0, 60) };
    if (type === "function") return { t: "function" };
    if (typeof Element !== "undefined" && v instanceof Element)
      // A text box or menu shows what's typed or picked in it, not its HTML.
      return { t: "element", tag: v.tagName.toLowerCase(), text: ("value" in v && /^(INPUT|TEXTAREA|SELECT)$/.test(v.tagName) ? String(v.value) : v.textContent || "").trim().slice(0, 40) };
    if (typeof CanvasRenderingContext2D !== "undefined" && v instanceof CanvasRenderingContext2D) return { t: "drawing" };
    if (Array.isArray(v) || (typeof NodeList !== "undefined" && v instanceof NodeList) || (typeof HTMLCollection !== "undefined" && v instanceof HTMLCollection)) {
      var first = v[0];
      if (typeof Element !== "undefined" && first instanceof Element) return { t: "list", count: v.length, tag: first.tagName.toLowerCase() };
      return { t: "list", count: v.length, text: fmt(Array.prototype.slice.call(v, 0, 8)).slice(0, 60) };
    }
    // A plain object reads as "x: 0, y: 540, size: 40".
    var parts = [];
    try {
      var names = Object.keys(v);
      for (var j = 0; j < names.length && j < 4; j++) {
        var item = v[names[j]], kind = typeof item;
        parts.push(names[j] + ": " + (kind === "number" || kind === "boolean" ? String(item) : kind === "string" ? "\u201c" + item.replace(/\s+/g, " ").slice(0, 12) + "\u201d" : Array.isArray(item) ? "list of " + item.length : "…"));
      }
      if (names.length > 4) parts.push("…");
    } catch (err) {}
    return { t: "object", text: parts.join(", ").slice(0, 60) };
  };
  var NAME = /^[A-Za-z_$][A-Za-z0-9_$]*$/;
  var watch = function (names) {
    var values = {}, raw = {};
    for (var i = 0; i < names.length && i < 40; i++) {
      var name = String(names[i]);
      if (!NAME.test(name)) continue;
      // Indirect eval runs in the global scope, where the student's top-level let/const live.
      try { raw[name] = (0, eval)(name); values[name] = describe(raw[name]); }
      catch (err) { values[name] = { t: "unset" }; }
    }
    // An element that sits in one of the watched lists reads as "hole 5".
    for (var n in values) {
      if (values[n].t !== "element") continue;
      for (var m in values) {
        if (values[m].t !== "list" || !raw[m]) continue;
        var idx = Array.prototype.indexOf.call(raw[m], raw[n]);
        if (idx >= 0) { values[n].in = { list: m, index: idx }; break; }
      }
    }
    return values;
  };
  // "Check my tests": a play-test script (written by the helper from the student's plain-words test)
  // runs here, inside the same sandbox as the student's code, with a small kit for playing the game.
  // Real clocks and timers, kept before t.speed can change the page's (the kit and the loop guard
  // always run on real time).
  var realNow = Date.now.bind(Date), realPerf = performance.now.bind(performance);
  var realSetTimeout = setTimeout.bind(window), realSetInterval = setInterval.bind(window), realClearTimeout = clearTimeout.bind(window);
  window.__blNow = realNow;
  var sleep = function (ms) { return new Promise(function (r) { realSetTimeout(r, Math.max(0, Math.min(Number(ms) || 0, 5000))); }); };
  // t.speed(factor): the game's clock runs factor times faster (timers, animation frames, Date.now,
  // performance.now), so "after 30 seconds" can be checked in a few real seconds.
  var speedUp = function (factor) {
    var f = Math.max(1, Math.min(Number(factor) || 1, 10));
    var baseReal = realNow(), baseFake = Date.now(), baseRealPerf = realPerf(), baseFakePerf = performance.now();
    Date.now = function () { return baseFake + (realNow() - baseReal) * f; };
    performance.now = function () { return baseFakePerf + (realPerf() - baseRealPerf) * f; };
    window.setTimeout = function (fn, ms) { var rest = Array.prototype.slice.call(arguments, 2); return realSetTimeout.apply(window, [fn, (Number(ms) || 0) / f].concat(rest)); };
    window.setInterval = function (fn, ms) { var rest = Array.prototype.slice.call(arguments, 2); return realSetInterval.apply(window, [fn, Math.max(4, (Number(ms) || 0) / f)].concat(rest)); };
    window.requestAnimationFrame = function (cb) { return realSetTimeout(function () { cb(performance.now()); }, 16 / f); };
    window.cancelAnimationFrame = function (id) { realClearTimeout(id); };
    return f;
  };
  var find = function (sel) {
    var el = document.querySelector(sel);
    if (!el) throw new Error("couldn't find " + sel + " on the page");
    return el;
  };
  // Data a check pretends getData returned (e.g. a cold, rainy day), so a rule can be checked
  // without waiting for that weather. Only for this run of the page.
  var pretendData = {};
  var sitePages = ${scriptJson(pages)};
  var kit = {
    // The names of the site's pages, and a read-only copy of one (its HTML as written, scripts not
    // run), so a check can compare menus and links on every page without leaving this one.
    speed: speedUp,
    pages: function () { return Object.keys(sitePages); },
    page: function (name) {
      var html = sitePages[String(name)];
      if (html == null) throw new Error("there's no page called " + name);
      return new DOMParser().parseFromString(html, "text/html");
    },
    wait: sleep,
    // Make getData(source) answer with this data (or fail with this message, if given an Error).
    pretend: function (source, data) { pretendData[String(source)] = { data: data }; },
    click: function (sel) {
      var el = find(sel);
      ["pointerdown", "mousedown", "pointerup", "mouseup"].forEach(function (type) {
        el.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true, view: window }));
      });
      el.click();
      return sleep(50);
    },
    press: function (key, ms) {
      var code = key.length === 1 ? (/[a-z]/i.test(key) ? "Key" + key.toUpperCase() : /[0-9]/.test(key) ? "Digit" + key : key === " " ? "Space" : key) : key;
      var target = document.activeElement || document.body;
      var fire = function (type) { target.dispatchEvent(new KeyboardEvent(type, { key: key, code: code, bubbles: true, cancelable: true })); };
      fire("keydown");
      return sleep(ms == null ? 100 : ms).then(function () { fire("keyup"); return sleep(30); });
    },
    // Wait until a selector exists (or a function returns true), up to ms (default 3000).
    waitFor: function (what, ms) {
      var until = realNow() + Math.min(Number(ms) || 3000, 8000);
      var ok = function () { try { return typeof what === "function" ? !!what() : !!document.querySelector(what); } catch (err) { return false; } };
      return new Promise(function (resolve) {
        var tick = function () { if (ok()) resolve(true); else if (realNow() > until) resolve(false); else realSetTimeout(tick, 50); };
        tick();
      });
    },
    // The smallest element whose text includes the given text (e.g. "🐹"), or null.
    findText: function (text, sel) {
      var all = document.querySelectorAll(sel || "body *"), best = null;
      for (var i = 0; i < all.length; i++) {
        if ((all[i].textContent || "").indexOf(text) >= 0 && (!best || best.contains(all[i]))) best = all[i];
      }
      return best;
    },
    clickText: function (text, sel) {
      var el = kit.findText(text, sel);
      if (!el) throw new Error("couldn't find '" + text + "' on the page");
      ["pointerdown", "mousedown", "pointerup", "mouseup"].forEach(function (type) {
        el.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true, view: window }));
      });
      el.click();
      return sleep(50);
    },
    // Type into an input or textarea (replacing what's there), or pick an option in a <select>.
    type: function (sel, value) {
      var el = find(sel), v = String(value == null ? "" : value);
      if (el.focus) el.focus();
      var proto = el.tagName === "SELECT" ? HTMLSelectElement.prototype : el.tagName === "TEXTAREA" ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
      var setter = Object.getOwnPropertyDescriptor(proto, "value");
      if (setter && setter.set) setter.set.call(el, v); else el.value = v;
      el.dispatchEvent(new Event("input", { bubbles: true }));
      el.dispatchEvent(new Event("change", { bubbles: true }));
      return sleep(50);
    },
    text: function (sel) { return (find(sel).textContent || "").trim(); },
    count: function (sel) { return document.querySelectorAll(sel).length; },
    exists: function (sel) { return !!document.querySelector(sel); },
    get: function (name) {
      if (!NAME.test(String(name))) throw new Error("get() takes a name like score");
      try { return copyOut((0, eval)(String(name))); } catch (err) { return undefined; }
    },
    canvas: function (sel) {
      var c = document.querySelector(sel || "canvas");
      if (!c || !c.getContext) return null;
      var data = c.getContext("2d").getImageData(0, 0, c.width, c.height).data, h = 2166136261;
      for (var i = 0; i < data.length; i += 16) h = Math.imul(h ^ data[i], 16777619);
      return (h >>> 0).toString(16);
    }
  };
  var copyOut = function (v) {
    if (v === null || typeof v !== "object") return v;
    try { return JSON.parse(JSON.stringify(v)); } catch (err) { return String(v); }
  };
  var runTest = function (id, script) {
    var done = false;
    var finish = function (pass, detail) {
      if (done) return;
      done = true;
      post({ kind: "test", id: id, pass: !!pass, detail: String(detail == null ? "" : detail).slice(0, 300) });
    };
    try {
      var fn = (0, eval)("(async function (t) {\\n" + script + "\\n})");
      Promise.resolve(fn(kit)).then(function (r) {
        if (r && typeof r === "object") finish(r.pass, r.detail);
        else finish(!!r, r ? "passed" : "didn't pass");
      }, function (err) { finish(false, "the check stopped: " + (err && err.message ? err.message : String(err))); });
    } catch (err) {
      finish(false, "the check couldn't run: " + (err && err.message ? err.message : String(err)));
    }
  };

  window.addEventListener("message", function (e) {
    if (e.source !== parent || !e.data) return;
    if (e.data.__buildlabPing) post({ kind: "pong", id: e.data.id });
    if (e.data.__buildlabTest && typeof e.data.script === "string") runTest(e.data.id, e.data.script);
    if (e.data.__buildlabData && dataWaiting[e.data.id]) {
      var waiting = dataWaiting[e.data.id];
      delete dataWaiting[e.data.id];
      if (e.data.ok) waiting.resolve(e.data.data);
      else waiting.reject(new Error(String(e.data.error || "getData didn't work.")));
    }
    if (e.data.__buildlabClassData && sharedWaiting[e.data.id]) {
      var answer = sharedWaiting[e.data.id];
      delete sharedWaiting[e.data.id];
      realClearTimeout(answer.timer);
      if (e.data.ok) answer.resolve(e.data.value === undefined ? null : e.data.value);
      else answer.reject(new Error(String(e.data.error || "The class data didn't work.")));
    }
    if (e.data.__buildlabWatch && Array.isArray(e.data.names)) {
      var copy = {};
      for (var k in runs) copy[k] = runs[k];
      post({ kind: "watch", id: e.data.id, values: watch(e.data.names), runs: copy });
    }
  });
  // Links can't take the preview to another website. (The app also resets the frame if it navigates.)
  document.addEventListener("click", function (e) {
    var a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
    if (!a) return;
    var href = a.getAttribute("href") || "";
    if (href.charAt(0) === "#") return;
    e.preventDefault();
    // A link to another page of this project (about.html) opens it in the preview (multi-page sites).
    var local = /^(?:\\.\\/)?([A-Za-z0-9_-]+\\.html?)(#.*)?$/.exec(href);
    if (local) { post({ kind: "navigate", page: local[1], hash: local[2] || "" }); return; }
    post({ kind: "blocked", what: "link", detail: href.slice(0, 200) });
    console.warn("BuildLab previews can't open other pages or websites (" + href.slice(0, 80) + ").");
  }, true);
  var store = ${scriptJson(savedData)};
  var copy = function (v) { return v === undefined ? null : JSON.parse(JSON.stringify(v)); };
  window.saveData = function (key, value) {
    key = String(key);
    store[key] = copy(value);
    post({ kind: "saveData", key: key, value: store[key] });
  };
  window.loadData = function (key) {
    var v = store[String(key)];
    return v === undefined ? null : copy(v);
  };
  // BuildLab Data: real data (weather, trivia, Bible verses) without internet access in the preview.
  // The app fetches it through BuildLab's server and answers here.
  var dataWaiting = {}, dataId = 0;
  window.getData = function (source, params) {
    var fake = pretendData[String(source)];
    if (fake) {
      return sleep(300).then(function () {
        if (fake.data instanceof Error) throw new Error(fake.data.message);
        return copy(fake.data);
      });
    }
    return new Promise(function (resolve, reject) {
      var id = ++dataId;
      dataWaiting[id] = { resolve: resolve, reject: reject };
      post({ kind: "getData", id: id, source: String(source), params: copy(params || {}) });
      realSetTimeout(function () {
        if (dataWaiting[id]) { delete dataWaiting[id]; reject(new Error("getData took too long to answer. Try again.")); }
      }, 20000);
    });
  };
  // Class-shared data (feature classData): one small store for the whole class. The app answers
  // each request with a ClassDataReply, matched by id; a missing answer times out like getData.
  var sharedWaiting = {}, sharedId = 0;
  var askShared = function (op, key, value) {
    return new Promise(function (resolve, reject) {
      var id = ++sharedId;
      var payload = { kind: "classData", id: id, op: op, key: String(key) };
      if (op === "set") payload.value = copy(value);
      var timer = realSetTimeout(function () {
        if (sharedWaiting[id]) { delete sharedWaiting[id]; reject(new Error("The class data took too long to answer. Try again.")); }
      }, 20000);
      sharedWaiting[id] = { resolve: resolve, reject: reject, timer: timer };
      post(payload);
    });
  };
  window.shareData = function (key, value) { return askShared("set", key, value).then(function () {}); };
  window.getShared = function (key) { return askShared("get", key); };
  window.__blLoopStop = function (file, line) {
    throw new Error("BuildLab stopped a loop in " + file + " on line " + line +
      " because it ran for more than a second. Check the loop's condition so it can finish.");
  };
  // Fit to screen (fit.ts): report the content's size whenever it or the frame changes. Height is the
  // bottom of the body (it can be less than the window); width is the scroll width (more than the
  // window means it doesn't fit sideways).
  var lastSize = "", sizeTimer = 0;
  var measure = function () {
    sizeTimer = 0;
    var de = document.documentElement, b = document.body;
    if (!de || !b) return;
    var vw = window.innerWidth, vh = window.innerHeight;
    var r = b.getBoundingClientRect(), cs = getComputedStyle(b);
    var h = r.bottom + (window.scrollY || 0) + (parseFloat(cs.marginBottom) || 0);
    if (de.scrollHeight > vh + 1) h = Math.max(h, de.scrollHeight);
    var size = { kind: "size", w: Math.ceil(de.scrollWidth), h: Math.ceil(h), vw: vw, vh: vh };
    var key = size.w + "," + size.h + "," + vw + "," + vh;
    if (key === lastSize) return;
    lastSize = key;
    post(size);
  };
  var queueMeasure = function () { if (!sizeTimer) sizeTimer = realSetTimeout(measure, 40); };
  window.addEventListener("resize", queueMeasure);
  window.addEventListener("load", queueMeasure);
  document.addEventListener("DOMContentLoaded", function () {
    queueMeasure();
    if (typeof ResizeObserver !== "undefined") {
      var ro = new ResizeObserver(queueMeasure);
      ro.observe(document.documentElement);
      if (document.body) ro.observe(document.body);
    }
  });
  window.addEventListener("keydown", function (e) { if (e.key === "Escape") post({ kind: "escape" }); }, true);
  post({ kind: "ready" });
})();`;
}
